import React, { useState, useEffect, useContext, useCallback } from "react";
import PageTitle from "../../Components/PageTitle";
import axios from "axios";
import Swal from "sweetalert2";
import { ConfigContext } from "../../Context/ConfigContext";
import { ContentLoader, NoRecords } from "../../Components/Shimmer";
import Select from "react-select";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { handleDownloadOptions } from "../../Components/ExportData";

const ClearenceRequiredList = () => {
    const { apiURL, apiHeaderJson, user_profile_id } = useContext(ConfigContext);

    // State for main list
    const [rows, setRows] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [next, setNext] = useState(false);
    const [prev, setPrev] = useState(false);

    // Modal states
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showActionModal, setShowActionModal] = useState(false);
    const [requestedInfo, setRequestedInfo] = useState({});
    const [consultantInfo, setConsultantInfo] = useState({});
    const [clearenceInfo, setClearenceInfo] = useState({});
    const [projectRequestId, setProjectRequestId] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [attachments, setAttachments] = useState([])

    // Action form states
    const [selectedAction, setSelectedAction] = useState("");
    const [comment, setComment] = useState("");
    const [consultantId, setConsultantId] = useState(null);
    const [consultantData, setConsultantData] = useState([]);

    const navigate = useNavigate();

    // ---------- Fetch Main List ----------
    const GetList = useCallback(async () => {
        setLoadingList(true);
        try {
            const response = await axios.get(
                `${apiURL}api/requests/GetAssignedClearance`,
                {
                    params: {
                        consultant_id: user_profile_id,
                        page: page
                    },
                    headers: apiHeaderJson
                }
            );

            const { data, next, prev, page: currentPage, total_records, total_pages, success } = response.data;

            if (success) {
                if (success) {
                    const filteredRows = (data || []).filter(
                        (row) => Number(row.request_status) === 4
                    );

                    setRows(filteredRows);
                    setNext(next);
                    setPrev(prev);
                    setPage(currentPage);
                    setTotalRecords(filteredRows.length);
                    setTotalPages(total_pages);
                }
            }
        } catch (error) {
            console.error("Error fetching clearance list:", error);
            Swal.fire("Error", "Failed to fetch clearance list", "error");
        } finally {
            setLoadingList(false);
        }
    }, [apiURL, apiHeaderJson, user_profile_id, page]);

    // Initial load and page change
    useEffect(() => {
        GetList();
    }, [GetList]);

    // ---------- Pagination ----------
    const handlePrev = () => {
        if (prev) {
            setPage((p) => p - 1);
        }
    };

    const handleNext = () => {
        if (next) {
            setPage((p) => p + 1);
        }
    };

    const handlePageChange = (e) => {
        const newPage = parseInt(e.target.value, 10);
        if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // ---------- Status UI ----------
    const GetStatus = (status) => {
        if (status == '0') {
            return "Pending"
        } else if (status == '1') {
            return "Approved"
        } else if (status == '2') {
            return "Revise"
        } else if (status == '3') {
            return "Rejected"
        } else if (status == '4') {
            return "In-Clearence"
        }
    }

    const GetBadge = (status) => {
        if (status == '0') {
            return "badge bg-warning-subtle text-warning"
        } else if (status == '1') {
            return "badge bg-success-subtle text-success"
        } else if (status == '2') {
            return "badge bg-info-subtle text-info"
        } else if (status == '3') {
            return "badge bg-danger-subtle text-danger"
        } else if (status == '4') {
            return "badge bg-secondary-subtle text-secondary"
        }

    }

    // ---------- Download Report ----------
    const handleDownload = (downloadType) => {
        const endpoint = `api/requests/GetAssignedClearance`;
        const params = {
            consultant_id: user_profile_id,
            page: 1 // Reset to first page for full download
        };

        const columns = [
            { header: "Project Name", width: 35 },
            { header: "Project Location", width: 25 },
            { header: "Project Description", width: 25 },
            { header: "Project Status", width: 20 }
        ];

        const mapRow = (data) => ({
            "Project Name": data?.project_title ?? "-",
            "Project Location": data?.project_address ?? "-",
            "Project Description": data?.project_description ?? "-",
            "Request Status": GetStatus(data?.project_status),
        });

        handleDownloadOptions(
            endpoint,
            params,
            columns,
            mapRow,
            downloadType,
            apiURL,
            apiHeaderJson
        );
    };

    // ---------- Modal Logic ----------
    const openInfoModal = (row) => {
        setRequestedInfo(row);
        GetInfo(row);
        setSelectedProjectId(row?.project_id);
        setProjectRequestId(row?.project_request_id || null);
        setShowInfoModal(true);
    };

    const closeInfoModal = () => {
        setShowInfoModal(false);
        setRequestedInfo({});
        setSelectedProjectId(null);
        setProjectRequestId(null);
    };

    const openActionModal = () => {
        setShowInfoModal(false);
        setShowActionModal(true);
        // Reset action form when opening
        resetActionForm();
    };

    const closeActionModal = () => {
        setShowActionModal(false);
        resetActionForm();
    };

    const resetActionForm = () => {
        setSelectedAction("");
        setComment("");
        setConsultantId(null);
    };

    const GetInfo = (row) => {
        const headers = apiHeaderJson;

        axios
            .get(`${apiURL}api/requests/GetProjectRequestInfo?project_request_id=${row?.project_request_id}`, {
                headers,
            })
            .then((response) => {
                const { data, success } = response.data;
                if (success) {
                    setAttachments(data.attachments)
                    setConsultantInfo(data.consultants[0])
                    setClearenceInfo(data.projects__request_clearance[0])
                }
            });
    };

    // ---------- Consultant Fetch ----------
    useEffect(() => {
        if (!selectedProjectId) {
            setConsultantData([]);
            return;
        }

        const getProjectConsultant = async () => {
            try {
                const response = await axios.get(
                    `${apiURL}api/project-consultants/project/${selectedProjectId}`,
                    { headers: apiHeaderJson }
                );

                const { data } = response.data;
                setConsultantData(
                    data.map((row) => ({
                        label: row.profile_name,
                        value: row.consultant_id,
                    }))
                );
            } catch (error) {
                console.error("Error fetching consultants:", error);
                setConsultantData([]);
            }
        };

        getProjectConsultant();
    }, [selectedProjectId, apiURL, apiHeaderJson]);

    // ---------- Action Submit ----------
    const handleActionSubmit = async () => {
        const statusMap = {
            "Direct Approve": "1",
            "Revise": "2",
            "Reject": "3",
        };

        if (!selectedAction) {
            Swal.fire("Warning", "Please select an action", "warning");
            return;
        }

        try {
            let response;

            if (selectedAction === "Clearance Required") {
                if (!consultantId) {
                    Swal.fire("Warning", "Please select a consultant", "warning");
                    return;
                }

                const formData = new FormData();
                formData.append("project_request_id", projectRequestId);
                formData.append("project_id", selectedProjectId);
                formData.append("consultant_id", consultantId);
                formData.append("assigned_by", user_profile_id);
                formData.append("clearance_remark", comment);

                response = await axios.post(
                    `${apiURL}api/requests/AssignForClearance`,
                    formData,
                    { headers: apiHeaderJson }
                );
            } else {
                const body = {
                    project_request_id: projectRequestId,
                    consultant_id: user_profile_id,
                    consultant_status: statusMap[selectedAction],
                    consultant_remark: comment
                };

                response = await axios.patch(
                    `${apiURL}api/project-items/status`,
                    body,
                    { headers: apiHeaderJson }
                );
            }

            const { success, message } = response.data;

            await Swal.fire(
                success ? "Success" : "Error",
                message,
                success ? "success" : "error"
            );

            if (success) {
                closeActionModal();
                GetList();
            }

        } catch (error) {
            console.error("Error submitting action:", error);
            Swal.fire("Error", "Something went wrong", "error");
        }
    };

    return (
        <div className="main-content">
            <div className="page-content">
                {!loadingList ? (
                    <div className="container-fluid">
                        <br />
                        <PageTitle title="Clearance List" primary="Home" />

                        <div className="card">
                            <div
                                className="card-header align-items-center d-flex"
                                style={{ background: "#000000" }}
                            >
                                <h4 className="card-title mb-0 flex-grow-1 text-white">
                                    Clearance List
                                </h4>
                            </div>

                            {/* DOWNLOAD BUTTON */}
                            <div className="card-header">
                                <div className="row justify-content-end mb-2">
                                    <div className="col-md-2">
                                        <div className="dropdown">
                                            <button
                                                className="btn btn-light btn-label waves-effect waves-light"
                                                data-bs-toggle="dropdown"
                                            >
                                                <i className="ri-download-2-line label-icon align-middle fs-16 me-2" />
                                                Download Report
                                            </button>

                                            <ul className="dropdown-menu dropdown-menu-end">
                                                <li>
                                                    <a
                                                        className="dropdown-item"
                                                        onClick={() => handleDownload("print")}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="ri-printer-line me-2 text-muted" />
                                                        Print
                                                    </a>
                                                </li>
                                                <li>
                                                    <a
                                                        className="dropdown-item"
                                                        onClick={() => handleDownload("pdf")}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="ri-file-pdf-line me-2 text-muted" />
                                                        PDF
                                                    </a>
                                                </li>
                                                <li>
                                                    <a
                                                        className="dropdown-item"
                                                        onClick={() => handleDownload("excel")}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="ri-file-excel-2-line me-2 text-muted" />
                                                        Excel
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* TABLE */}
                            <div className="card-body">
                                <div className="table-responsive table-card">
                                    <table className="table table-bordered table-stripped mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="text-center">Project Name</th>
                                                <th className="text-center">Project Location</th>
                                                <th className="text-center">Project Description</th>
                                                <th className="text-center">Request Status</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>

                                        {rows.length > 0 ? (
                                            <tbody>
                                                {rows.map((row) => (
                                                    <tr key={row.project_id}>
                                                        <td className="text-center">
                                                            <NavLink to={`/ProjectProfile/${row.project_id}`}>
                                                                {row.project_title}
                                                            </NavLink>
                                                        </td>

                                                        <td className="text-center">{row.project_address}</td>

                                                        <td className="text-center">{row.project_description}</td>

                                                        <td className="text-center">
                                                            <span className={GetBadge(row.request_status)}>
                                                                {GetStatus(row.request_status)}
                                                            </span>
                                                        </td>

                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-link text-info fs-18 p-0"
                                                                onClick={() => openInfoModal(row)}
                                                            >
                                                                <i className="ri-eye-line" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        ) : (
                                            <tbody>
                                                <tr>
                                                    <td colSpan="5">
                                                        <NoRecords />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        )}

                                        {/* PAGINATION FOOTER */}
                                        <tfoot className="table-light">
                                            <tr>
                                                <th colSpan={5}>
                                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                        <button
                                                            disabled={!prev}
                                                            type="button"
                                                            onClick={handlePrev}
                                                            className="btn btn-warning btn-label waves-effect waves-light"
                                                        >
                                                            <i className="ri-arrow-left-line label-icon me-2" />
                                                            Previous
                                                        </button>

                                                        <small className="text-muted">
                                                            Total Records: {totalRecords} | Total Pages:{" "}
                                                            {totalPages} | Current Page: {page}
                                                        </small>

                                                        <select
                                                            className="form-select w-auto"
                                                            onChange={handlePageChange}
                                                            value={page}
                                                            style={{ maxWidth: "100px" }}
                                                        >
                                                            {Array.from({ length: totalPages }, (_, i) => (
                                                                <option key={i + 1} value={i + 1}>
                                                                    Page {i + 1}
                                                                </option>
                                                            ))}
                                                        </select>

                                                        <button
                                                            disabled={!next}
                                                            type="button"
                                                            onClick={handleNext}
                                                            className="btn btn-primary btn-label waves-effect waves-light"
                                                        >
                                                            Next
                                                            <i className="ri-arrow-right-line label-icon ms-2" />
                                                        </button>
                                                    </div>
                                                </th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <ContentLoader />
                )}

                {/* ---------- Project Info Modal ---------- */}
                {showInfoModal && (
                    <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                                <div
                                    className="modal-header d-flex justify-content-between align-items-center"
                                    style={{ background: "#000000" }}
                                >
                                    <div className="w-100">
                                        <div className="row">
                                            <div className="col-md-4">
                                                <h5 className="mb-0 text-white">
                                                    Title: {requestedInfo?.project_title ?? "-"}
                                                </h5>
                                            </div>
                                            <div className="col-md-4">
                                                <h5 className="mb-0 text-white">
                                                    Start Date: {requestedInfo?.project_start_date ?? "-"}
                                                </h5>
                                            </div>
                                            <div className="col-md-4">
                                                <h5 className="mb-0 text-white">
                                                    End Date: {requestedInfo?.project_end_date ?? "-"}
                                                </h5>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeInfoModal}
                                        style={{ filter: "invert(1)" }}
                                    />
                                </div>

                                <div className="modal-body">
                                    {/* Item Details */}
                                    <div className="mb-3 p-3 border rounded bg-white">
                                        <h6 className="text-primary mb-2">Item Details</h6>
                                        <div className="row">
                                            <div className="col-md-3">
                                                <strong>Item Name:</strong> {requestedInfo?.item_name || "-"}
                                            </div>
                                            <div className="col-md-9">
                                                <strong>Type of Submittal:</strong>{" "}
                                                {requestedInfo?.submittal_description || "-"}
                                            </div>
                                            <div className="col-md-12 mt-3">
                                                <strong>Brief Description:</strong>{" "}
                                                {requestedInfo?.request_brief_description || "-"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section: Uploaded Files */}
                                    <div className="mb-3 p-3 border rounded bg-light">
                                        <h6 className="text-primary mb-2">Attachments</h6>
                                        {attachments.length > 0 ? (
                                            <div className="d-flex flex-wrap gap-3">
                                                {attachments.map((file, index) => {
                                                    const fileUrl = file.attachment_path.replace(/\\/g, '');
                                                    const lowerUrl = fileUrl.toLowerCase();
                                                    const isPdf = lowerUrl.endsWith('.pdf');
                                                    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(lowerUrl);

                                                    return (
                                                        <a
                                                            key={index}
                                                            href={fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                            style={{ width: '100px' }}
                                                        >
                                                            <div className="p-2 bg-white rounded  d-flex flex-column align-items-center border">
                                                                <i
                                                                    className={`ri-${isPdf
                                                                        ? 'file-pdf-fill text-danger'
                                                                        : isImage
                                                                            ? 'image-fill text-primary'
                                                                            : 'file-fill text-secondary'
                                                                        } fs-1 mb-1`}
                                                                ></i>
                                                                <small className="text-truncate text-center text-muted" style={{ maxWidth: '80px' }}>
                                                                    {isPdf ? 'PDF Document' : isImage ? 'Image File' : 'Other File'}
                                                                </small>
                                                            </div>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0">No File Uploaded</p>
                                        )}
                                    </div>

                                    {/* Requester Details */}
                                    <div className="mb-3 p-3 border rounded bg-white">
                                        <h6 className="text-primary mb-2">
                                            Clearance Requested From
                                        </h6>

                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <strong>Name:</strong>{" "}
                                                {requestedInfo?.profile_name || "-"}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>Email:</strong>{" "}
                                                {requestedInfo?.profile_email || "-"}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>Contact:</strong>{" "}
                                                {requestedInfo?.profile_contact_number || "-"}
                                            </div>
                                            <div className="col-md-4">
                                                <strong>Comment:</strong>{" "}
                                                {clearenceInfo?.clearance_remark || "-"}
                                            </div>
                                            <div className="col-md-4">
                                                <Link to={`/ClearenceList/ViewSubmittedInspectionForm/${requestedInfo?.inspection_id}`}>View Inspection Form</Link>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action */}
                                    <div className="d-flex justify-content-end mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={openActionModal}
                                        >
                                            Take Action
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* -------- Action Modal -------- */}
                {showActionModal && (
                    <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Take Action</h5>
                                    <button
                                        className="btn-close"
                                        onClick={closeActionModal}
                                    />
                                </div>

                                <div className="modal-body">
                                    <div className="d-flex flex-wrap gap-2 mb-3 justify-content-between">
                                        {["Direct Approve", "Revise", "Reject", "Clearance Required"].map(
                                            (action) => (
                                                <button
                                                    key={action}
                                                    type="button"
                                                    className={`btn ${selectedAction === action
                                                        ? "btn-primary"
                                                        : "btn-outline-primary"
                                                        } flex-fill`}
                                                    onClick={() => setSelectedAction(action)}
                                                >
                                                    {action}
                                                </button>
                                            )
                                        )}
                                    </div>

                                    {selectedAction === "Clearance Required" && (
                                        <div className="mb-3">
                                            <Select
                                                options={consultantData}
                                                onChange={(opt) =>
                                                    setConsultantId(opt ? opt.value : null)
                                                }
                                                value={
                                                    consultantData.find(
                                                        (opt) => opt.value === consultantId
                                                    ) || null
                                                }
                                                placeholder="Select Consultant"
                                                isClearable
                                            />
                                        </div>
                                    )}

                                    <textarea
                                        className="form-control"
                                        placeholder="Comment / Remark"
                                        rows="4"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    />
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-warning"
                                        onClick={resetActionForm}
                                    >
                                        Clear
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeActionModal}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleActionSubmit}
                                    >
                                        Submit Action
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClearenceRequiredList;